import { Pipe, PipeTransform, inject } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { MarkdownService } from 'ngx-markdown';

export interface MarkdownWithColorContext {
  chatUniqueId?: string;
  sessionId?: string;
  documentOrigins?: any[];
}

@Pipe({
  name: 'markdownWithColor',
  standalone: true,
  pure: true, // Keep it pure for performance
})
export class MarkdownWithColorPipe implements PipeTransform {
  #markdownService = inject(MarkdownService);
  #sanitizer = inject(DomSanitizer);

  async transform(
    value: string,
    context?: MarkdownWithColorContext,
  ): Promise<SafeHtml> {
    if (!value) {
      return '';
    }

    // Remove the origins marker (used to trigger pipe re-evaluation)
    const cleanValue = value.replace(/<!--\s*origins:\d+\s*-->$/, '');

    console.log('🔄 Processing markdown for message:', context?.chatUniqueId, {
      docOriginsCount: context?.documentOrigins?.length || 0,
    });

    // Step 1: Decode Unicode escape sequences first
    let processed = this.decodeUnicodeEscapes(cleanValue);

    // Step 2: Parse markdown
    const htmlContent = await this.#markdownService.parse(processed);

    // Step 3: Replace doc references AFTER markdown processing (only in HTML, not in code blocks)
    let withDocReferences = htmlContent;
    if (context?.chatUniqueId && context?.documentOrigins?.length) {
      console.log(
        '📄 Replacing doc references with',
        context.documentOrigins.length,
        'origins',
      );
      withDocReferences = this.replaceDocReferencesWithLinks(
        htmlContent,
        context.chatUniqueId,
        context.documentOrigins,
      );
    } else {
      console.log('⚠️ No doc origins available yet');
    }

    // Step 4: Replace color tags AFTER markdown processing
    const withColorTags = this.replaceColorTags(withDocReferences);

    // Step 5: Return sanitized HTML (bypass sanitizer for our trusted HTML)
    return this.#sanitizer.bypassSecurityTrustHtml(withColorTags);
  }

  /**
   * Decodes Unicode escape sequences like \uD83D\uDCCA to actual characters
   */
  private decodeUnicodeEscapes(text: string): string {
    try {
      return text.replace(/\\[uU]([0-9A-Fa-f]{4})/g, (match, grp) => {
        return String.fromCharCode(parseInt(grp, 16));
      });
    } catch (error) {
      console.error('Error decoding Unicode escapes:', error);
      return text;
    }
  }

  /**
   * Replaces [bg:#color] and [fg:#color] tags with visual HTML elements
   * This happens AFTER markdown processing to avoid being escaped
   */
  private replaceColorTags(html: string): string {
    // Replace background color tags: [bg:#color]
    html = html.replace(
      /\[bg:(#[0-9A-Fa-f]{6}|#[0-9A-Fa-f]{3})\]/g,
      (match, color) => {
        return `<span class="color-indicator" style="display: inline-block; width: 16px; height: 16px; background-color: ${color}; border: 1px solid rgba(0,0,0,0.2); border-radius: 2px; vertical-align: middle; margin: 0 2px;"></span>`;
      },
    );

    // Replace foreground/text color tags: [fg:#color]
    html = html.replace(
      /\[fg:(#[0-9A-Fa-f]{6}|#[0-9A-Fa-f]{3})\]/g,
      (match, color) => {
        // Empty return or you can add a text color indicator if needed
        return '';
      },
    );

    return html;
  }

  /**
   * Replaces [doc_X] and [doc_X, doc_Y, ...] references with clickable links
   * This happens AFTER markdown processing so links inside code blocks remain as text
   * @param html The HTML content from markdown processing
   * @param chatUniqueId The unique ID of the chat message
   * @param documentOrigins The document origins for this message
   * @returns HTML with doc references replaced by links
   */
  private replaceDocReferencesWithLinks(
    html: string,
    chatUniqueId: string,
    documentOrigins: any[],
  ): string {
    // Pattern to match [doc_1], [doc_2], [doc_1, doc_2], [doc_1,doc_2], doc_6, doc_6, doc_7, etc.
    // But only match them when they're NOT inside <code> or <pre> tags
    const docPattern = /\[?doc_\d+(?:\s*,\s*doc_\d+)*\]?/gi;

    // Replace doc references with links
    return html.replace(docPattern, (match, offset) => {
      // Extract all document numbers from the reference
      const docNumbers = match
        .replace(/[\[\]]/g, '') // Remove brackets
        .split(',')
        .map((ref: string) => ref.trim().replace(/doc_/i, ''))
        .filter((num: string) => num)
        .map((num: string) => parseInt(num, 10));

      if (!documentOrigins || documentOrigins.length === 0) {
        // Origins not available yet, return placeholder
        console.log('  ⏭️ Skipping (no origins)');
        return match;
      }

      // Get the actual documents referenced
      const referencedDocs = docNumbers
        .map((num: number) => documentOrigins[num - 1]) // Convert 1-based to 0-based index
        .filter((doc: any) => doc && doc.fileSha256); // Only include valid documents with SHA

      // Remove duplicates based on fileSha256
      const uniqueDocs = referencedDocs.filter(
        (doc: any, index: number, self: any[]) =>
          index === self.findIndex((d) => d.fileSha256 === doc.fileSha256),
      );

      if (uniqueDocs.length === 0) {
        // No valid documents found
        console.log('  ⏭️ Skipping (no valid docs)');
        return match;
      }

      console.log('  ✅ Replacing with link');

      // Build the href with comma-separated fileSha256 values
      const fileShas = uniqueDocs.map((doc: any) => doc.fileSha256).join(',');

      // For multiple documents, add a special class and data attribute to trigger menu
      if (uniqueDocs.length > 1) {
        const firstDoc = uniqueDocs[0];
        const firstName = firstDoc.fileName || 'Document';
        const additionalCount = uniqueDocs.length - 1;
        const linkText = `${firstName} +${additionalCount}`;

        return `<a href="#doc-ref-${chatUniqueId}-${fileShas}" class="doc-reference-link doc-reference-multiple" data-doc-count="${uniqueDocs.length}">${linkText}</a>`;
      } else {
        // Single document - direct link
        const firstName = uniqueDocs[0].fileName || 'Document';
        return `<a href="#doc-ref-${chatUniqueId}-${fileShas}" class="doc-reference-link doc-reference-single">${firstName}</a>`;
      }
    });
  }
}
